import { useCallback, useEffect, useState } from 'react'
import {
  supportApi,
  type TicketListItem,
  type SupportTicket,
  type CreateTicketRequest,
} from '../services/api'
import { useAuth } from '../contexts/AuthContext'

export function useTickets(projectId: string | undefined) {
  const { user } = useAuth()
  const [tickets, setTickets] = useState<TicketListItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [statusFilter, setStatusFilter] = useState<string | undefined>()

  const loadTickets = useCallback(async () => {
    if (!user || !projectId) return
    setLoading(true)
    try {
      const data = await supportApi.listProjectTickets(projectId, { status: statusFilter })
      setTickets(data)
      setError(null)
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load tickets')
    } finally {
      setLoading(false)
    }
  }, [user, projectId, statusFilter])

  useEffect(() => {
    loadTickets()
  }, [loadTickets])

  return { tickets, loading, error, statusFilter, setStatusFilter, refresh: loadTickets }
}

export function useTicketDetail(ticketId: string | undefined) {
  const { user } = useAuth()
  const [ticket, setTicket] = useState<SupportTicket | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const loadTicket = useCallback(async () => {
    if (!user || !ticketId) return
    setLoading(true)
    try {
      const data = await supportApi.getTicket(ticketId)
      setTicket(data)
      setError(null)
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load ticket')
    } finally {
      setLoading(false)
    }
  }, [user, ticketId])

  useEffect(() => {
    loadTicket()
  }, [loadTicket])

  const addResponse = async (content: string) => {
    if (!ticketId) return
    await supportApi.addResponse(ticketId, content)
    await loadTicket()
  }

  const updateStatus = async (status: string) => {
    if (!ticketId) return
    await supportApi.updateStatus(ticketId, status)
    await loadTicket()
  }

  const createTicket = async (data: CreateTicketRequest) => {
    const created = await supportApi.createTicket(data)
    return created
  }

  return { ticket, loading, error, addResponse, updateStatus, createTicket, refresh: loadTicket }
}
